import { KnowledgeArticle, createArticle } from '../types'

export const settingsArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'organization-settings',
    title: 'Organization Settings',
    section: 'settings',
    summary: 'Configure your organization name, website, domains for monitoring, and compliance frameworks. Settings affect platform-wide behavior.',
    content: `
# Organization Settings

Configure your organization's profile and platform-wide settings from the Settings page.

## Organization Profile

### Basic Information
- **Organization Name** - Your company name
- **Website** - Primary website domain
- **Industry** - Business sector for compliance context
- **Description** - Brief description of your organization

### Contact Information
- **Primary Contact** - Main contact for compliance matters
- **Email** - Contact email address
- **Phone** - Contact phone number

## Monitored Domains

Add domains your organization owns for:
- Dark web monitoring
- Self-assessment scans
- TPRM integration

### Adding Domains
1. Go to Settings > Domains
2. Click **Add Domain**
3. Enter the domain (e.g., company.com)
4. Verify ownership (optional)
5. Domain is now monitored

## Compliance Frameworks

Select frameworks relevant to your organization:
- **SOC 2** - Service organization controls
- **ISO 27001** - Information security management
- **PCI DSS** - Payment card security
- **HIPAA** - Healthcare data privacy
- **GDPR** - EU data protection
- **NIST CSF** - Cybersecurity framework

### Framework Selection
1. Go to Settings > Frameworks
2. Enable/disable frameworks
3. Controls populate based on selection
4. Mappings show cross-framework alignment

## Feature Configuration

### Evidence Settings
- Default evidence retention period
- Auto-link evidence to controls
- Evidence approval workflow (on/off)

### Task Settings
- Default task priority
- Overdue notification timing
- Auto-create tasks from control schedules

### Notification Settings
- Email notification preferences
- Daily/weekly digest options
- Alert thresholds
    `.trim(),
    tags: ['settings', 'organization', 'configuration', 'frameworks', 'domains'],
    relatedPages: ['/settings'],
    icon: 'Settings',
    order: 1,
  }),

  createArticle({
    id: 'integration-settings',
    title: 'Integration Settings',
    section: 'settings',
    summary: 'Manage cloud integrations (AWS, Azure), Atlassian connection, and scanner configuration. Test connections and view sync status.',
    content: `
# Integration Settings

Manage external system connections from the Integrations page.

## Cloud Integrations

### AWS
Connect AWS accounts for evidence collection:
- IAM users and roles
- EC2 instances
- Security groups
- RDS databases
- S3 bucket configurations

**Status Indicators:**
- Green - Connected and syncing
- Yellow - Connected but sync issues
- Red - Connection failed

### Azure
Connect Azure subscriptions:
- Entra ID users and groups
- Virtual machines
- Network security groups
- SQL databases
- Storage accounts

## Atlassian Integration

### Connecting
1. Click **Connect Atlassian**
2. Authorize via OAuth popup
3. Grant requested permissions
4. Connection established

### Jira Configuration
After connecting:
- Map Jira projects to GRC
- Set default issue types
- Enable two-way sync
- Configure auto-evidence

### Confluence Configuration
- Map Confluence spaces
- Set document type mapping
- Enable auto-import
- Configure import schedule

## Scanner Configuration

### Scanner Service
- **URL** - Scanner service endpoint
- **Webhook Secret** - Shared authentication key
- **Status** - Connection health

### Testing
Click **Test Connection** to verify:
- Service is reachable
- Authentication works
- Scanner is operational

## Managing Integrations

### Viewing Status
Each integration shows:
- Connection status
- Last sync time
- Error messages (if any)
- Sync history

### Refreshing
Click **Sync Now** to manually trigger:
- Data collection from cloud providers
- Import from Confluence
- Refresh of cached data

### Disconnecting
To remove an integration:
1. Click the integration
2. Click **Disconnect**
3. Confirm the action
4. Connection is removed
5. Collected data is retained

## Troubleshooting

### Common Issues

**AWS: Access Denied**
- Verify IAM policy is attached
- Check access key is active
- Ensure correct region

**Azure: Invalid Credentials**
- Client secret may be expired
- Create new secret and update

**Atlassian: Authorization Failed**
- Re-authorize the connection
- Check admin permissions
- Verify OAuth app is enabled

See the Troubleshooting article for detailed steps.
    `.trim(),
    tags: ['settings', 'integrations', 'aws', 'azure', 'atlassian', 'scanner', 'configuration'],
    relatedPages: ['/settings', '/integrations'],
    icon: 'Link',
    order: 2,
  }),

  createArticle({
    id: 'audit-logs',
    title: 'Audit Logs',
    section: 'settings',
    summary: 'View audit logs of all user actions: logins, data changes, evidence uploads, and configuration changes. Filter by user, action type, or date.',
    content: `
# Audit Logs

The audit log provides a complete record of all activities in the platform for compliance and security purposes.

## What's Logged

### User Actions
- Login and logout events
- Password/authentication changes
- Profile updates

### Data Changes
- Create, update, delete of any record
- Control status changes
- Evidence uploads and modifications
- Document changes

### Configuration Changes
- Integration connections
- Settings modifications
- User management actions
- Framework changes

## Viewing Audit Logs

1. Go to **Settings > Audit Logs** (admin only)
2. View the activity feed
3. Use filters to narrow results

### Filters
- **User** - Filter by specific user
- **Action Type** - Create, Update, Delete, Login, etc.
- **Resource Type** - Controls, Evidence, Users, etc.
- **Date Range** - Time period to search

## Log Entry Details

Each entry includes:
- **Timestamp** - When the action occurred
- **User** - Who performed the action
- **Action** - What was done (create, update, delete)
- **Resource** - What was affected
- **Details** - Specific changes made
- **IP Address** - Where the action originated

## Searching Logs

### By User
Find all actions by a specific person:
1. Select user from dropdown
2. View their activity history

### By Resource
Find all changes to a specific item:
1. Filter by resource type
2. Search by resource name or ID

### By Time
Find activity in a specific period:
1. Set start and end dates
2. View chronological activity

## Exporting Logs

Export audit logs for external review:
1. Apply desired filters
2. Click **Export**
3. Choose format (CSV, JSON)
4. Download the file

## Retention

Audit logs are retained for:
- **Active period** - 2 years of searchable logs
- **Archive period** - Additional 5 years in cold storage
- **Compliance** - Meets SOC 2 and ISO 27001 requirements

## Best Practices

1. Review logs periodically
2. Investigate unusual patterns
3. Export for audits proactively
4. Correlate with security events
5. Use for incident investigation
    `.trim(),
    tags: ['settings', 'audit', 'logs', 'compliance', 'security', 'tracking'],
    relatedPages: ['/settings'],
    icon: 'ScrollText',
    order: 3,
  }),
]
